import React, { useEffect, useState } from 'react';
import Button from 'antd/lib/button';
import Form from 'antd/lib/form';
import { Row } from 'antd/lib/grid';
import Input from 'antd/lib/input';
import Modal from 'antd/lib/modal';
import { FranchiseeCenterLayout } from 'common/components/Layouts/FranchiseeCenterLayout';
import { ReactComponent as LockIcon } from 'app/assets/icons/lockIcon.svg';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { resetPayMongoAPIKeys, setPayMongoAPIKeys, setUserModel } from 'app/store/reducers/user.reducer';

export const APIKeysPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { payMongoState, payMongoStateLoading } = useAppSelector((state) => state.user);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [otpModalState, setOtpModalState] = useState<{ action?: string; visible: boolean }>({ action: '', visible: false });
  const [code, setCode] = useState<string>('');
  const isPayMongoAPIKeysSet = Boolean(payMongoState && payMongoState.isPublicKeySet && payMongoState.isSecretKeySet);

  const [form] = Form.useForm();

  const showCodeModal = (action: string) => {
    setOtpModalState({ action, visible: true });
  };

  const closeCodeModal = () => {
    setCode('');
    setOtpModalState({ visible: false });
  };

  const resetEditForm = () => {
    form.resetFields();
    setIsEditMode(false);
    closeCodeModal();
  };

  const handleSaveKeys = () => {
    const values = form.getFieldsValue(['publicKey', 'secretKey']);

    dispatch(
      setPayMongoAPIKeys({ otp: code, publicKey: values.publicKey, secretKey: values.secretKey }, resetEditForm, closeCodeModal)
    );
  };

  const handleResetKeys = () => {
    dispatch(resetPayMongoAPIKeys({ otp: code }, resetEditForm, closeCodeModal));
  };

  const handleKeys = () => {
    if (otpModalState.action === 'set') {
      handleSaveKeys();

      return;
    }

    if (otpModalState.action === 'reset') {
      handleResetKeys();

      return;
    }
  };

  useEffect(() => {
    return () => dispatch(setUserModel(null));
  }, []);

  return (
    <>
      <Modal
        open={otpModalState.visible}
        okText="Confirm"
        onOk={handleKeys}
        okButtonProps={{ disabled: !code.trim() || payMongoStateLoading }}
        cancelText="Cancel"
        onCancel={closeCodeModal}
        className="twoFA-modal t-align-c"
      >
        <LockIcon className="mt-4 mb-4" />
        <p>Warning! You are about to change this store&apos;s API Keys.</p>
        <p>Please enter 6-digit code generated by Google Authenticator and click Confirm to proceed.</p>

        <Input name="code" type="text" value={code} placeholder="Security code" onChange={(e) => setCode(e.target.value)} />
      </Modal>
      <FranchiseeCenterLayout>
        <Row justify="center" className="mb-4">
          <h1 className="franchisee__text_title fs-md" title="API Keys">
            API Keys
          </h1>
        </Row>
        <p className="franchisee__text_default">
          To be able to use the payment methods, you need to connect your PayMongo account to MBGo account. Your API keys are
          available on the Developers tab of the{' '}
          <a
            className="font-weight-4 basic__link"
            href="https://dashboard.paymongo.com/developers"
            target="_blank"
            rel="noreferrer"
          >
            dashboard
          </a>
        </p>
        <Form layout="vertical" form={form}>
          <Form.Item
            rules={[
              {
                required: true,
                message: isPayMongoAPIKeysSet ? 'Enter new public key' : 'Public Key is required',
              },
            ]}
            name="publicKey"
            label="Public Key"
          >
            <Input.Password
              name="publicKey"
              type="password"
              disabled={payMongoStateLoading || (isPayMongoAPIKeysSet && !isEditMode)}
              placeholder={isPayMongoAPIKeysSet ? '********' : 'Public Key'}
            />
          </Form.Item>
          <Form.Item
            rules={[
              {
                required: true,
                message: isPayMongoAPIKeysSet ? 'Enter new secret key' : 'Secret Key is required',
              },
            ]}
            name="secretKey"
            label="Secret Key"
          >
            <Input.Password
              name="secretKey"
              type="password"
              disabled={payMongoStateLoading || (isPayMongoAPIKeysSet && !isEditMode)}
              placeholder={isPayMongoAPIKeysSet ? '********' : 'Secret Key'}
            />
          </Form.Item>
          {isEditMode ? (
            <Row>
              <Form.Item className="mb-0">
                <Button className="button_default" onClick={resetEditForm} disabled={payMongoStateLoading}>
                  Cancel
                </Button>
              </Form.Item>

              <Form.Item className="mb-0 ml-4">
                <Button
                  onClick={() => showCodeModal('set')}
                  type="primary"
                  className="button_default"
                  disabled={payMongoStateLoading}
                >
                  Save
                </Button>
              </Form.Item>
            </Row>
          ) : isPayMongoAPIKeysSet ? (
            <>
              <Form.Item className="mb-0 flex-justify-center">
                <Button
                  type="primary"
                  className="button_default"
                  onClick={() => setIsEditMode(true)}
                  disabled={payMongoStateLoading}
                >
                  Edit Keys
                </Button>
              </Form.Item>
              <div
                onClick={() => showCodeModal('reset')}
                className="t-align-c font-family__default fs-xxs basic__link ellipsis cursor-pointer mt-5"
              >
                Reset the keys to MBGo Leslie keys
              </div>
            </>
          ) : (
            <Form.Item className="mb-0 flex-justify-center">
              <Button
                onClick={() => showCodeModal('set')}
                type="primary"
                className="button_default"
                disabled={payMongoStateLoading}
              >
                Connect
              </Button>
            </Form.Item>
          )}
        </Form>
      </FranchiseeCenterLayout>
    </>
  );
};
